import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Navbar } from './Navbar';
import { FlatworldIcon } from '../common/FlatworldIcon';
import cloudsImage from '../../assets/ghibli/clouds.svg';
import mountainsImage from '../../assets/ghibli/mountains.svg';
import squareImage from '../../assets/shape/Square.png';

// 从环境变量读取 VITE_VERSION
const VITE_VERSION = import.meta.env.VITE_VERSION;

export const Layout = ({
  children,
  isAuthenticated,
  userProfile,
  onSignIn,
  onSignOut,
}) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50">
      <div className="absolute inset-0">
        {/* 吉卜力风格天空背景 */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-100 via-blue-50 to-white"></div>
        
        {/* 云朵层 */}
        <div 
          className="fixed inset-0 bg-repeat-x bg-left-top opacity-30 animate-drift"
          style={{ backgroundImage: `url(${cloudsImage})` }}
        ></div>
        
        {/* 远山层 - 仅在底部显示 */}
        <div 
          className="fixed bottom-0 left-0 right-0 h-64 bg-repeat-x bg-bottom opacity-20"
          style={{ backgroundImage: `url(${mountainsImage})` }}
        ></div>
        
        {/* 纹理层 */}
        <div className="absolute inset-0 mix-blend-overlay opacity-5" 
          style={{ 
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` 
          }}>
        </div>
        
        {/* 圆形形状角色 - 右下角装饰 */}
        <div 
          className="fixed bottom-10 right-10 w-32 h-32 bg-contain bg-no-repeat opacity-40 pointer-events-none"
          style={{ backgroundImage: `url(${squareImage})` }}
        ></div>
      </div>
      
      <Navbar
        isAuthenticated={isAuthenticated}
        userProfile={userProfile}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
      />
      
      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full relative z-10 animate-fade-in">
        {/* 吉卜力风格卡片样式 */}
        <style>{`
          .ghibli-card {
            @apply relative overflow-hidden rounded-xl bg-white/90 shadow-md border border-neutral-200/50 p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg;
          }
          .ghibli-card::before {
            content: '';
            @apply absolute inset-0 bg-noise opacity-10;
            background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          }
          .ghibli-icon {
            @apply w-16 h-16 rounded-full bg-primary-100 text-primary-600 p-3 flex items-center justify-center shadow-inner mb-4 relative;
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          
          @keyframes sway {
            0%, 100% { transform: rotate(-1deg); }
            50% { transform: rotate(1deg); }
          }
          
          @keyframes drift {
            from { background-position: 0% 0; }
            to { background-position: 100% 0; }
          }
        `}</style>

        {children}
      </main>
      
      <footer className="relative z-10 mt-24 bg-white/80 border-t border-neutral-200/80 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="py-12">
            <div className="flex flex-col gap-8">
              {/* Brand section */}
              <div>
                <Link to="/" className="flex items-center gap-2 group">
                  <FlatworldIcon className="w-8 h-8" />
                  <span className="text-xl font-bold text-neutral-800 group-hover:text-primary-600 transition-all">
                    Flatworld { VITE_VERSION }
                  </span>
                </Link>
                <p className="mt-6 text-base text-neutral-700 leading-relaxed max-w-2xl">
                  {t('footer.description')}
                </p>
              </div>

              {/* Links and social */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
                <div className="flex flex-wrap gap-x-8 gap-y-4">
                  <a href="https://doc.flatland.app/" className="text-sm text-neutral-700 hover:text-primary-600 transition-colors">
                    {t('footer.sections.resources.documentation')}
                  </a>
                  <a href="/guides" className="text-sm text-neutral-700 hover:text-primary-600 transition-colors">
                    {t('footer.sections.resources.guides')}
                  </a>
                  <a href="https://github.com/flatlandapp" className="text-sm text-neutral-700 hover:text-primary-600 transition-colors">
                    {t('footer.sections.resources.apiReference')}
                  </a>
                </div>

                <div className="flex items-center gap-4">
                  <a
                    href="https://github.com/flatlandapp"
                    className="group flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 bg-white shadow-sm transition-all hover:border-primary-300 hover:shadow-md hover:scale-110"
                  >
                    <svg className="h-4 w-4 text-neutral-700 transition-colors group-hover:text-primary-600" fill="currentColor" viewBox="0 0 24 24">
                      <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                    </svg>
                  </a>
                  <a
                    href="https://twitter.com/FlatworldAI"
                    className="group flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 bg-white shadow-sm transition-all hover:border-secondary-300 hover:shadow-md hover:scale-110"
                  >
                    <svg className="h-4 w-4 text-neutral-700 transition-colors group-hover:text-secondary-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  </a>
                  <a
                    href="https://discord.gg/YQpKPnHF"
                    className="group flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 bg-white shadow-sm transition-all hover:border-accent-300 hover:shadow-md hover:scale-110"
                  >
                    <svg className="h-4 w-4 text-neutral-700 transition-colors group-hover:text-accent-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 00-.079.036c-.21.369-.444.85-.608 1.23a18.566 18.566 0 00-5.487 0 12.36 12.36 0 00-.617-1.23A.077.077 0 008.562 3c-1.714.29-3.354.8-4.885 1.491a.07.07 0 00-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 00.031.055 20.03 20.03 0 005.993 2.98.078.078 0 00.084-.026 13.83 13.83 0 001.226-1.963.074.074 0 00-.041-.104 13.201 13.201 0 01-1.872-.878.075.075 0 01-.008-.125c.126-.093.252-.19.372-.287a.075.075 0 01.078-.01c3.927 1.764 8.18 1.764 12.061 0a.075.075 0 01.079.009c.12.098.245.195.372.288a.075.075 0 01-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 00-.041.105c.36.687.772 1.341 1.225 1.962a.077.077 0 00.084.028 19.963 19.963 0 006.002-2.981.076.076 0 00.032-.054c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 00-.031-.028zM8.02 15.278c-1.182 0-2.157-1.069-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.069-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.946 2.38-2.157 2.38z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-neutral-200 py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-neutral-600">
                {t('footer.copyright', { year: currentYear })}
              </p>
              <div className="flex items-center gap-1">
                <span className="text-sm text-neutral-600">{t('footer.builtWith')}</span>
                <span className="text-accent-500 animate-pulse">❤</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
